/**
 * On-device viewport diagnostic overlay.
 *
 * Issue #249: Chrome on iOS lays the app out under its own top chrome when the
 * native Kegtron app hands back to /add-device?claim=... The displacement is
 * invisible to the DOM (see chromeIosRelayout.js), only reproduces on a real
 * phone launched from another app, and the CSP forbids beaconing measurements
 * to a LAN collector — so the only way to get numbers off the device is to
 * paint them on the page and screenshot them.
 *
 * OPT-IN AND PER-DEVICE. Nothing renders unless `?ktdiag=1` has been loaded
 * once on that browser (`?ktdiag=0` clears it). The flag lives in localStorage,
 * so it survives the hop out to the native app and back into the claim URL —
 * which is the load we need to measure and the one we cannot add a param to.
 *
 * This is developer-facing data, not product UI: every line is a key and a
 * number, so there is no prose to translate.
 */

import { getBuildVersion } from '../services/versionCheckService';
import {
	buildExperimentPanel,
	readExpParam,
	resolveExpEnabled,
	EXP_PARAM
} from './viewportExperiments';

export const DIAG_PARAM = 'ktdiag';
export const DIAG_STORAGE_KEY = 'kt_viewport_diag';

/** Samples are taken at boot and again once layout has settled. */
export const DIAG_SETTLE_DELAY_MS = 600;

/**
 * Sit ABOVE the footer nav (--footer-height, 56px on phones) rather than on
 * top of it. The overlay is also click-through: the whole point of this build
 * is to reach Add Device in the footer and run the real hand-off, so the
 * diagnostic must never intercept a tap. Disarm with `?ktdiag=0`.
 */
export const DIAG_BOTTOM_OFFSET_PX = 60;

const OVERLAY_ID = 'kt-viewport-diag';

/** The href as it arrived, captured before anything can rewrite it. */
let bootHref = null;

/** Call as the very first thing in start-up, before the relayout tidies markers. */
export function captureBootHref(href) {
	if (href != null) {
		bootHref = href;
	} else if (typeof window !== 'undefined') {
		bootHref = window.location.href;
	}
	return bootHref;
}

/** `?ktdiag=1` → 'on', `?ktdiag=0` → 'off', absent → null. */
export function readDiagParam(search) {
	const raw = new URLSearchParams(search || '').get(DIAG_PARAM);
	if (raw === null) return null;
	return raw === '0' || raw === 'off' ? 'off' : 'on';
}

/** Resolve the flag for this load, applying any `?ktdiag=` instruction. */
export function resolveDiagEnabled(search, storage) {
	const instruction = readDiagParam(search);
	try {
		if (instruction === 'on') {
			storage.setItem(DIAG_STORAGE_KEY, '1');
			return true;
		}
		if (instruction === 'off') {
			storage.removeItem(DIAG_STORAGE_KEY);
			return false;
		}
		return storage.getItem(DIAG_STORAGE_KEY) === '1';
	} catch (e) {
		// Private mode / storage blocked: honour the param for this load only.
		return instruction === 'on';
	}
}

/**
 * Read the four `env(safe-area-inset-*)` values, via a throwaway probe element
 * (they are only reachable through CSS). Issue #249 hinges on these: Chrome
 * positions the web-view frame UNDER its own top toolbar while reporting a
 * correctly-sized viewport, so if WebKit accounts for that frame at all, a
 * non-zero top inset is the only place it can surface — and it would give both
 * the detection AND the exact correction the layout needs.
 */
export function readSafeAreaInsets(doc) {
	const probe = doc.createElement('div');
	const s = probe.style;
	s.position = 'fixed';
	s.top = '0';
	s.left = '0';
	s.width = '0';
	s.height = '0';
	s.visibility = 'hidden';
	s.paddingTop = 'env(safe-area-inset-top)';
	s.paddingRight = 'env(safe-area-inset-right)';
	s.paddingBottom = 'env(safe-area-inset-bottom)';
	s.paddingLeft = 'env(safe-area-inset-left)';
	doc.body.appendChild(probe);
	const cs = (doc.defaultView || window).getComputedStyle(probe);
	const insets = {
		top: parseFloat(cs.paddingTop) || 0,
		right: parseFloat(cs.paddingRight) || 0,
		bottom: parseFloat(cs.paddingBottom) || 0,
		left: parseFloat(cs.paddingLeft) || 0
	};
	probe.remove();
	return insets;
}

function rect(el) {
	if (!el || typeof el.getBoundingClientRect !== 'function') return null;
	const r = el.getBoundingClientRect();
	return { top: Math.round(r.top), bottom: Math.round(r.bottom), h: Math.round(r.height) };
}

/** One measurement of everything the page can see about its own viewport. */
export function collectDiagSnapshot(label, win) {
	const w = win || window;
	const doc = w.document;
	const vv = w.visualViewport || null;
	const app = doc.getElementById('app');
	const sa = doc.body ? readSafeAreaInsets(doc) : null;
	return {
		label,
		// Does anything outside the layout-viewport APIs see the 121pt the frame
		// is displaced by? These two are the only remaining candidates.
		safeArea: sa ? { top: sa.top, bottom: sa.bottom } : null,
		screenY: typeof w.screenY === 'number' ? w.screenY : w.screenTop,
		innerH: w.innerHeight,
		clientH: doc.documentElement ? doc.documentElement.clientHeight : null,
		outerH: w.outerHeight,
		screenH: w.screen ? w.screen.height : null,
		scrollY: w.scrollY,
		vvH: vv ? Math.round(vv.height) : null,
		vvOffsetTop: vv ? Math.round(vv.offsetTop) : null,
		vvPageTop: vv ? Math.round(vv.pageTop) : null,
		vvScale: vv ? vv.scale : null,
		app: rect(app),
		header: rect(doc.querySelector('header')),
		dpr: w.devicePixelRatio
	};
}

/** Flatten a snapshot to one `k=v k=v` line. */
export function formatSnapshot(snap) {
	const parts = [];
	Object.keys(snap).forEach((k) => {
		const v = snap[k];
		if (v === null || v === undefined) return;
		if (k === 'label') return;
		if (typeof v === 'object') {
			parts.push(
				'h' in v ? `${k}[${v.top}..${v.bottom} h${v.h}]` : `${k}[t${v.top} b${v.bottom}]`
			);
		} else {
			parts.push(`${k}=${v}`);
		}
	});
	return `${snap.label}: ${parts.join(' ')}`;
}

function styleOverlay(el) {
	const s = el.style;
	s.position = 'fixed';
	s.left = '0';
	s.right = '0';
	s.bottom = `${DIAG_BOTTOM_OFFSET_PX}px`;
	s.zIndex = '2147483647';
	s.pointerEvents = 'none';
	s.maxHeight = '35%';
	s.overflow = 'auto';
	s.background = 'rgba(0,0,0,0.88)';
	s.color = '#0f0';
	s.font = '10px/1.35 Menlo, monospace';
	s.padding = '6px 8px';
	s.whiteSpace = 'pre-wrap';
	s.wordBreak = 'break-all';
	s.border = '1px solid #0f0';
}

function overlayLines(win) {
	const w = win || window;
	return [
		`build=${getBuildVersion()}`,
		`boot=${bootHref || w.location.href}`,
		`now=${w.location.href}`,
		`ua=${(w.navigator.userAgent || '').replace(/^.*?(CriOS|Version)\//, '$1/').slice(0, 48)}`
	];
}

function paint(win, snapshots, withPanel) {
	const w = win || window;
	const doc = w.document;
	let el = doc.getElementById(OVERLAY_ID);
	if (!el) {
		el = doc.createElement('div');
		el.id = OVERLAY_ID;
		styleOverlay(el);
		const text = doc.createElement('div');
		text.id = `${OVERLAY_ID}-text`;
		el.appendChild(text);
		if (withPanel) {
			// The panel carries its own pointer-events; the overlay stays
			// click-through so it can never swallow a tap meant for the app.
			el.appendChild(buildExperimentPanel(doc, w, null));
		}
		doc.body.appendChild(el);
	}
	doc.getElementById(`${OVERLAY_ID}-text`).textContent = overlayLines(w)
		.concat(snapshots.map(formatSnapshot))
		.join('\n');
}

/**
 * Start the diagnostic if this device opted in. Safe to call on every boot.
 * @returns {boolean} true if the overlay was armed
 */
export function initViewportDiag(win) {
	const w = win || (typeof window !== 'undefined' ? window : null);
	if (!w) return false;
	// One flag, deliberately. A separate `ktexp` flag meant arming it required a
	// build that already understood the param — and a device still running the
	// previous bundle silently dropped it, so the panel never appeared on the
	// very device the arming was meant for. Either param arms the overlay, and
	// the overlay always carries the panel.
	const enabled =
		resolveDiagEnabled(w.location.search, w.localStorage) ||
		resolveExpEnabled(w.location.search, w.localStorage);

	// Tidy our own param off the URL — whether it armed or disarmed — so it
	// never rides into a shared link. The flag has already been applied.
	if (readDiagParam(w.location.search) || readExpParam(w.location.search)) {
		try {
			const u = new URL(w.location.href);
			u.searchParams.delete(DIAG_PARAM);
			u.searchParams.delete(EXP_PARAM);
			w.history.replaceState(w.history.state, '', u.toString());
		} catch (e) {
			// Harmless: the param only sets the flag, it is never read again.
		}
	}

	if (!enabled) return false;

	const snapshots = [];
	const sample = (label) => {
		snapshots.push(collectDiagSnapshot(label, w));
		if (w.document.body) paint(w, snapshots, true);
	};

	const start = () => {
		sample('boot');
		w.setTimeout(() => sample(`+${DIAG_SETTLE_DELAY_MS}ms`), DIAG_SETTLE_DELAY_MS);
		w.addEventListener('resize', () => sample('resize'));
		w.addEventListener('orientationchange', () => sample('rotate'));
		// The manual fix is a tab switch, so record what the page sees when the
		// frame is re-attached — for comparison with the displaced samples.
		w.document.addEventListener('visibilitychange', () => {
			if (!w.document.hidden) sample('visible');
		});
	};

	if (w.document.readyState === 'loading') {
		w.document.addEventListener('DOMContentLoaded', start);
	} else {
		start();
	}
	return true;
}
